import type { Page, Locator } from 'playwright';
import { buildCursorSvg, buildHighlightCss, CURSOR_ELEMENT_ID } from './cursor-visuals.js';

/**
 * Driver-driven cursor overlay for ref-based actions.
 *
 * The driver imperatively glides the cursor to each action's target (via the
 * resolved locator's `boundingBox`) and draws a ripple/glow BEFORE performing the
 * action — the anticipatory "glide-in then click" motion. Used by the per-action
 * methods in `PlaywrightClient` (click/type/hover/drag/scroll) and by ref-based
 * cache replay.
 *
 * Fast-drive (`run_code`) uses a separate, event-driven `AutonomousCursor` instead
 * — see `autonomous-cursor.ts`. The two are independent and never both animate the
 * same action (see the autonomous switch in `executeRunCode`). Both share the same
 * brand visuals via `cursor-visuals.ts`.
 */

const HIGHLIGHT_DURATION_MS = 150;

type HighlightType = 'click' | 'type' | 'hover' | 'drag-start' | 'drag-end';

interface CursorOverlayConfig {
  speedPxPerMs: number;
  minDurationMs: number;
  maxDurationMs: number;
}

interface CursorOverlayOptions {
  enabled?: boolean;
  speedPxPerMs?: number;
  minDurationMs?: number;
  maxDurationMs?: number;
}

export class CursorOverlay {
  private lastX = 0;
  private lastY = 0;
  private injected = false;
  private highlightStylesInjected = false;
  private enabled: boolean;
  private config: CursorOverlayConfig;

  constructor(opts?: CursorOverlayOptions) {
    this.enabled = opts?.enabled ?? true;
    this.config = {
      speedPxPerMs: opts?.speedPxPerMs ?? 1.5,
      minDurationMs: opts?.minDurationMs ?? 100,
      maxDurationMs: opts?.maxDurationMs ?? 600,
    };
  }

  private async inject(page: Page): Promise<void> {
    if (this.injected) return;

    try {
      await page.evaluate(
        ({ svg, id, x, y }) => {
          if ((window as any).__chatsdetCursorInstalled) return;
          (window as any).__chatsdetCursorInstalled = true;

          const container = document.createElement('div');
          container.id = id;
          container.innerHTML = svg;
          container.style.cssText = [
            'position: fixed',
            'pointer-events: none',
            'z-index: 9999999',
            `left: ${x}px`,
            `top: ${y}px`,
            'transition-property: left, top',
            'transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1)',
            'transition-duration: 0s',
          ].join(';');
          document.body.appendChild(container);
        },
        { svg: buildCursorSvg(), id: CURSOR_ELEMENT_ID, x: this.lastX, y: this.lastY }
      );
      this.injected = true;
    } catch {
      // Never block the action
    }
  }

  async moveTo(page: Page, x: number, y: number): Promise<void> {
    if (!this.enabled) return;

    try {
      await this.inject(page);

      const dx = x - this.lastX;
      const dy = y - this.lastY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const duration = Math.min(
        this.config.maxDurationMs,
        Math.max(this.config.minDurationMs, distance / this.config.speedPxPerMs)
      );

      await page.evaluate(
        ({ id, x, y, durationMs }) => {
          const el = document.getElementById(id);
          if (!el) return;
          el.style.transitionDuration = `${durationMs}ms`;
          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
        },
        { id: CURSOR_ELEMENT_ID, x, y, durationMs: Math.round(duration) }
      );

      await page.waitForTimeout(Math.round(duration));

      this.lastX = x;
      this.lastY = y;
    } catch {
      // Never block the action
    }
  }

  async moveToElement(page: Page, locator: Locator): Promise<void> {
    if (!this.enabled) return;

    try {
      const box = await locator.boundingBox();
      if (!box) return;
      const cx = Math.round(box.x + box.width / 2);
      const cy = Math.round(box.y + box.height / 2);
      await this.moveTo(page, cx, cy);
    } catch {
      // Never block the action
    }
  }

  private async injectHighlightStyles(page: Page): Promise<void> {
    if (this.highlightStylesInjected) return;
    await page.evaluate((css) => {
      if ((window as any).__chatsdetHighlightInstalled) return;
      (window as any).__chatsdetHighlightInstalled = true;
      const style = document.createElement('style');
      style.textContent = css;
      document.head.appendChild(style);
    }, buildHighlightCss());
    this.highlightStylesInjected = true;
  }

  async highlightElement(page: Page, locator: Locator, type: HighlightType): Promise<void> {
    if (!this.enabled) return;

    try {
      await this.injectHighlightStyles(page);

      const box = await locator.boundingBox();
      if (!box) return;

      const highlightType = type.startsWith('drag') ? 'hover' : type;
      const isClick = type === 'click';
      const left = isClick ? box.x + box.width / 2 : box.x - 4;
      const top = isClick ? box.y + box.height / 2 : box.y - 4;
      const width = box.width + 8;
      const height = box.height + 8;

      await page.evaluate(
        ({ left, top, width, height, highlightType, duration, isClick }) => {
          const el = document.createElement('div');
          el.className = `chatsdet-highlight chatsdet-highlight--${highlightType}`;
          el.style.left = `${left}px`;
          el.style.top = `${top}px`;
          if (!isClick) {
            el.style.width = `${width}px`;
            el.style.height = `${height}px`;
          }
          document.body.appendChild(el);
          setTimeout(() => el.remove(), duration + 100);
        },
        { left, top, width, height, highlightType, duration: HIGHLIGHT_DURATION_MS, isClick }
      );

      await page.waitForTimeout(HIGHLIGHT_DURATION_MS);
    } catch {
      // Never block the action
    }
  }

  async highlightPoint(page: Page, x: number, y: number, type: 'click' | 'hover'): Promise<void> {
    if (!this.enabled) return;

    try {
      await this.injectHighlightStyles(page);

      await page.evaluate(
        ({ x, y, highlightType, duration }) => {
          const el = document.createElement('div');
          el.className = `chatsdet-highlight chatsdet-highlight--${highlightType}`;
          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
          document.body.appendChild(el);
          setTimeout(() => el.remove(), duration + 100);
        },
        { x, y, highlightType: type, duration: HIGHLIGHT_DURATION_MS }
      );

      await page.waitForTimeout(HIGHLIGHT_DURATION_MS);
    } catch {
      // Never block the action
    }
  }

  resetInjectionState(): void {
    this.injected = false;
    this.highlightStylesInjected = false;
  }
}
